import { useLocation, useParams } from "react-router-dom";
import { blueCheckbox, machineBreakdownGrey } from "../../constants/colors";
import { useGetIsOperatingSystemWindows } from "utils/customHooks/useGetIsOperatingSystemWindows";
import NeedGeAndLt from "./Buttons/NeedGeAndLt";
import ReportHeader from "./ReportHeader";
import {
  FORMAT_READABLE_DATE,
  getEpochToReadableTime,
  ONE_DAY_OF_MS,
} from "reports/utils/timeUtils";
import styled from "styled-components/macro";
import { isEmpty } from "lodash";
import PrintButton from "./Buttons/PrintButton";
import LoadingCircle from "reports/components/simple/LoadingCircle";
import {
  FlightTrendsButton,
  FlightTimelineButton,
} from "./Buttons/FlightButtons";
import {
  PrintableReportGlobalStyles,
  PrintableReportStyles,
  PrinterOnlyRavenLogo,
} from "./printableReportContentStyles";
import { useGetShifts } from "reports/utils/shiftsHook";
import MidgardPnlBreakdownGetter from "../../getters/pnlBreakdownGetter/PnlBreakdownGetter";
import AskrPnlBreakdownGetter from "../../getters/pnlBreakdownGetter/AskrPnlBreakdownGetter";
import { PnlBreakdownFiltersEnum } from "../../getters/pnlBreakdownGetter/pnlBreakdownTypes";
import {
  useGeAndLtFromUrl,
  useProcessGroupNameAndMachemetryIds,
} from "./reportUtils";
import { useTranslation } from "react-i18next";
import LanguageSelector from "reports/components/LanguageSelector";
import { useFlightDashboardLinksByMachemetryId } from "reports/utils/machemetryUtils";
import {
  flightDashboardLinksAtom,
  pnlBreakdownDataAtom,
  pnlBreakdownsByProcessAtom,
  pnlBreakdownsBySKUAtom,
  pnlBreakdownsByShiftAtom,
  lastNWeekDataAtom,
  pnlBreakdownByShiftAndProcessAtom,
} from "../../store/store";
import { useAtom } from "jotai";
import LastNWeekGetter from "./BespokeReports/Getters/LastNWeekGetter";
import { EOS_TOP_PADDING_PX } from "./eosConstants";
import { useShouldFetchFromAskr } from "./useShouldFetchFromAskr";
import { useEffect } from "react";
import { useState } from "react";
import { trace } from "@opentelemetry/api";
import { useAuth0 } from "contexts/AuthManager/Auth";
import { errorOverlayStateAtom } from "reports/components/ErrorOverlay";
import { LinearProgress } from "@mui/material";
import { ReportParams } from "reports/types";
import { useServiceStatus } from "components/SimpleComponents/TopBanner/useServiceStatus";
import { useObserveComponentLoadTime } from "App/FlightPerformanceObserver";
import { useReportConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import CSVDownloadButton, {
  CSVRow,
  parseBreakdownToCSVRow,
} from "components/SimpleComponents/CSVDownloadButton";
import { format } from "date-fns";
import { shiftsEosReportAtom, useEOSReportHeader } from "./SummaryMetrics";
import ErrorBoundary from "components/SimpleComponents/Errors/ErrorBoundary";

const ReportStyles = styled.div`
  background-color: ${machineBreakdownGrey};
  border-radius: 3px;

  .timelineButton {
    width: 60%;
    margin: 0 auto;
    padding: ${EOS_TOP_PADDING_PX}px 0;
    width: fit-content;

    a {
      text-decoration: none;
    }

    button {
      border: 1px solid ${blueCheckbox};
      color: ${blueCheckbox};
    }
  }
  .actionButtons {
    display: flex;
    justify-content: end;
    gap: 0.25em;
  }
`;

const getEOSFileTitle = ({
  customTitle,
  tenant,
  ge,
  customTitlePrefix,
}: {
  customTitle?: string;
  tenant: string;
  ge: number;
  customTitlePrefix?: string;
}) => {
  const dateString = getEpochToReadableTime({
    epochTime: ge,
    formatString: FORMAT_READABLE_DATE,
  });

  return (
    "Raven Reports | " +
    (customTitlePrefix ?? "") +
    (customTitle ? customTitle : tenant.toUpperCase()) +
    " EOS Report " +
    dateString
  );
};

type EOSReportProps = {
  /**all machemetryIds required for report  Note: All the machemetryIds should belong to a single workSchedule*/
  machemetryIds: string[];
  /**set if the report requires a custom title */
  customTitle?: string;
  /**prepend customer name e.g. "Guttergloce — " to the report title*/
  prependCustomerNameToTitle?: string;
  /**set if the report requires a custom title */
  customSubTitle?: string;
  /**link with timestamps, i.e. `/${processGroupName}?ge=1607518800000&lt=1607547600000` */
  sampleLink: string;
  /**group to lead us to timeline page on flight, if applicable */
  flightTimelineLinkGroup?: string;
  /** group to lead us to dashboard page on flight, if applicable*/
  flightDashboardLinkGroup?: string;
  /**set if we want to show the trends page for the weekly report rather than the timeline page */
  showTrendsForMultiDay?: boolean;
  shouldShowPrint?: boolean;
  shouldShowDownload?: boolean;
  shouldShowTranslate?: boolean;
  /**set to false if report requires sku data*/
  shouldSkipSkuData?: boolean;
  /** set to true if the report requires to display shift Breakdown */
  shouldFetchDataByShift?: boolean;
  /** set to true if the report requires to display shift Breakdown per process (Eg. Michelin)*/
  shouldFetchDataByShiftAndProcess?: boolean;
  /** set to true if the report requires to show weekly trend */
  shouldFetchLastNWeeksData?: boolean;
  /** add the shifts to be filtered  */
  shiftsToExclude?: string[];
  numOfWeeks?: number;
  /** components to display in report */
  children: any;
};

// tslint:disable-next-line: cognitive-complexity
export default function EOSReport({
  machemetryIds,
  customTitle,
  customSubTitle,
  sampleLink,
  flightTimelineLinkGroup,
  flightDashboardLinkGroup,
  showTrendsForMultiDay = false,
  shouldShowPrint = true,
  shouldShowDownload = true,
  shouldShowTranslate = false,
  shouldSkipSkuData = true,
  shouldFetchDataByShift = false,
  shouldFetchDataByShiftAndProcess = false,
  shouldFetchLastNWeeksData = false,
  shiftsToExclude = [],
  numOfWeeks = 10,
  children,
}: EOSReportProps) {
  const location = useLocation();
  const { t } = useTranslation("EOSReport");
  const { ckey: tenant } = useParams() as ReportParams;
  const isWindows = useGetIsOperatingSystemWindows();

  const [flightDashboardLinks] = useAtom(flightDashboardLinksAtom);
  const shouldWaitForFlightLinks = useFlightDashboardLinksByMachemetryId(
    machemetryIds,
    flightDashboardLinkGroup
  );

  const [pnlBreakdownData, setPnlBreakdownData] = useAtom(pnlBreakdownDataAtom);
  const [pnlBreakdownsByProcess, setPnlBreakdownsByProcess] = useAtom(
    pnlBreakdownsByProcessAtom
  );
  const [pnlBreakdownsBySKU, setPnlBreakdownsBySKU] = useAtom(
    pnlBreakdownsBySKUAtom
  );
  const [pnlBreakdownsByShift, setPnlBreakdownsByShift] = useAtom(
    pnlBreakdownsByShiftAtom
  );
  const [pnlBreakdownByShiftAndProcess, setPnlBreakdownByShiftAndProcess] =
    useAtom(pnlBreakdownByShiftAndProcessAtom);

  const [lastNWeekData] = useAtom(lastNWeekDataAtom);

  const { ge, lt } = useGeAndLtFromUrl();

  const [shiftsData] = useAtom(shiftsEosReportAtom);

  // charts no longer load on the timeline page if > 4 days is requested
  // trends page is useful when report time range exceeds this
  const isMultiday = (lt - ge) / ONE_DAY_OF_MS > 4;

  // get distinct shift names
  const shiftsDataWithinRange = shiftsData.filter(
    shift => ge <= Number(shift.range.ge) && Number(shift.range.ge) < lt
  );
  const allShiftNamesWithinRange = shiftsDataWithinRange.map(({ shift }) => {
    return shift.name;
  });

  const shiftNamesWithinRange = allShiftNamesWithinRange
    .filter(
      (shiftName, index, shiftNamesArr) =>
        shiftNamesArr.indexOf(shiftName) == index
    )
    .filter(shiftName => !shiftsToExclude.includes(shiftName ? shiftName : ""));
  const reportConfig = useReportConfig();
  const customTitlePrefix =
    reportConfig?.eosReportGeneralConfig?.customTitlePrefix;
  document.title = getEOSFileTitle({
    customTitle,
    tenant,
    ge,
    customTitlePrefix,
  });

  const areAllPnlBreakdownsByProcessFetched =
    pnlBreakdownsByProcess.length >= machemetryIds.length &&
    pnlBreakdownsByProcess.every(elem => !isEmpty(elem));
  const numPnlBreakdownsByProcessRequired = machemetryIds.length;
  const numPnlBreakdownsByProcessLoaded = pnlBreakdownsByProcess.filter(
    elem => !isEmpty(elem)
  ).length;

  // If we are skipping sku data request, we don't need to wait for them to be fetched
  const areAllPnlBreakdownsBySkuFetched = shouldSkipSkuData
    ? true
    : pnlBreakdownsBySKU.length >= machemetryIds.length &&
      pnlBreakdownsBySKU.every(elem => !isEmpty(elem));
  const numPnlBreakdownsBySkuRequired = shouldSkipSkuData
    ? 0
    : machemetryIds.length;
  const numPnlBreakdownsBySkuLoaded = pnlBreakdownsBySKU.filter(
    elem => !isEmpty(elem)
  ).length;

  const areAllPnlBreakdownsByShiftFetched = !shouldFetchDataByShift
    ? true
    : pnlBreakdownsByShift.length >= shiftNamesWithinRange.length &&
      pnlBreakdownsByShift.every(elem => !isEmpty(elem));
  const numPnlBreakdownsByShiftRequired = shouldFetchDataByShift
    ? shiftNamesWithinRange.length
    : 0;
  const numPnlBreakdownsByShiftLoaded = pnlBreakdownsByShift.filter(
    elem => !isEmpty(elem)
  ).length;

  const areAllLastNWeeksPnlBreakdownsFetched = !shouldFetchLastNWeeksData
    ? true
    : lastNWeekData.length == numOfWeeks &&
      lastNWeekData.every(elem => !isEmpty(elem));
  const numLastNWeeksPnlBreakdownsRequired = shouldFetchLastNWeeksData
    ? numOfWeeks
    : 0;
  const numLastNWeeksPnlBreakdownsLoaded = lastNWeekData.filter(
    elem => !isEmpty(elem)
  ).length;

  const expectedNumPnlBreakdownsByShiftProcess =
    shiftNamesWithinRange.length * machemetryIds.length;
  shiftNamesWithinRange.length * machemetryIds.length;

  const areAllPnlBreakdownByShiftAndProcessFetched =
    !shouldFetchDataByShiftAndProcess
      ? true
      : pnlBreakdownByShiftAndProcess.length >=
          expectedNumPnlBreakdownsByShiftProcess &&
        pnlBreakdownByShiftAndProcess.every(elem => !isEmpty(elem));
  const numPnlBreakdownByShiftAndProcessRequired =
    shouldFetchDataByShiftAndProcess
      ? expectedNumPnlBreakdownsByShiftProcess
      : 0;

  const numPnlBreakdownByShiftAndProcessLoaded =
    pnlBreakdownByShiftAndProcess.filter(elem => !isEmpty(elem)).length;

  const hasFlightGroupAndMachemetryLinks =
    flightDashboardLinkGroup &&
    flightDashboardLinks &&
    flightDashboardLinks.length > 0;
  const numFlightLinksRequired =
    flightDashboardLinkGroup &&
    shouldWaitForFlightLinks &&
    hasFlightGroupAndMachemetryLinks
      ? machemetryIds.length
      : 0;
  const numFlightLinksLoaded = flightDashboardLinks.length;

  /** Flight links are done loading if:
   * 1) flightDashboardLinkGroup was provided
   * 2) shouldWaitForFlightLinks (we have valid group from machemetry
   * 3) all links from machemetry have loaded
   *
   * This has been made to be a one-way switch, so that once it's true,
   * it stays true.
   */
  const [areFlightLinksDoneLoading, setAreFlightLinksDoneLoading] =
    useState(false);

  useEffect(() => {
    const doneLoading =
      !flightDashboardLinkGroup ||
      !shouldWaitForFlightLinks ||
      hasFlightGroupAndMachemetryLinks;
    if (doneLoading && !areFlightLinksDoneLoading) {
      setAreFlightLinksDoneLoading(true);
    }
  }, [
    areFlightLinksDoneLoading,
    flightDashboardLinkGroup,
    hasFlightGroupAndMachemetryLinks,
    shouldWaitForFlightLinks,
  ]);

  const isDoneLoading =
    !isEmpty(pnlBreakdownData) &&
    areAllPnlBreakdownsByProcessFetched &&
    areAllPnlBreakdownsBySkuFetched &&
    areAllPnlBreakdownsByShiftFetched &&
    areAllPnlBreakdownByShiftAndProcessFetched &&
    areAllLastNWeeksPnlBreakdownsFetched &&
    areFlightLinksDoneLoading;

  useObserveComponentLoadTime({
    name: "EOSReport",
    loading: !isDoneLoading,
  });

  const { user } = useAuth0();
  const url = `${window.location.origin}${window.location.pathname}${window.location.search}${window.location.hash}`;

  // wait for askr data, if it doesn't come back in time, try midgard instead
  const { shouldFetchFromAskr, askrTimedOut } = useShouldFetchFromAskr(
    Boolean(isDoneLoading),
    "EOSReport"
  );

  // - trace when askr times out
  useEffect(() => {
    if (askrTimedOut) {
      console.log("✍ Askr timed out - sending trace to Honeycomb");
      const tracer = trace.getTracer("eos_report");
      const root_span = tracer.startSpan("askr_timed_out");
      root_span.setAttribute("machemetryIds", machemetryIds);
      root_span.setAttribute("ge", ge);
      root_span.setAttribute("lt", lt);
      root_span.setAttribute("email", user?.email ?? "");
      root_span.setAttribute("url", url);
      root_span.end();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [askrTimedOut]);

  const percentLoaded = getPercentLoaded({
    numPnlBreakdownsByProcessLoaded,
    numPnlBreakdownsByProcessRequired,
    numPnlBreakdownsBySkuLoaded,
    numPnlBreakdownsBySkuRequired,
    numPnlBreakdownsByShiftLoaded,
    numPnlBreakdownsByShiftRequired,
    numLastNWeeksPnlBreakdownsLoaded,
    numLastNWeeksPnlBreakdownsRequired,
    numPnlBreakdownByShiftAndProcessLoaded,
    numPnlBreakdownByShiftAndProcessRequired,
    numFlightLinksLoaded,
    numFlightLinksRequired,
  });

  // a report "failed" if it didn't load in time (if there's an actual error message, we'll instead show that through ServiceOutageIndicator instead)
  const [servicesStatus] = useServiceStatus();
  const failedToLoad = useFailedToLoad(
    Boolean(isDoneLoading && !servicesStatus.midgard.breakerOpen)
  );

  // - trace when a report failed
  useEffect(() => {
    if (failedToLoad) {
      console.log("✍ Report failed to load - sending trace to Honeycomb");
      const tracer = trace.getTracer("eos_report");
      const root_span = tracer.startSpan("failed_to_load");
      root_span.setAttribute("machemetryIds", machemetryIds);
      root_span.setAttribute("ge", ge);
      root_span.setAttribute("lt", lt);
      root_span.setAttribute("email", user?.email ?? "");
      root_span.setAttribute("url", url);

      root_span.end();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [failedToLoad]);
  const { processGroupName } = useProcessGroupNameAndMachemetryIds({});

  const customTitleFromUrl = new URLSearchParams(window.location.search).get(
    "customReportTitle"
  );

  const { reportHeader, readyForShifts } = useEOSReportHeader({
    processGroupName,
    customReportTitle: customTitleFromUrl ?? undefined,
    is24HourReport: lt - ge === ONE_DAY_OF_MS,
  });

  const haveShifts = shiftNamesWithinRange.length > 0;

  // EOS needs ge and lt url params
  if (location.search === "") {
    return (
      <NeedGeAndLt
        sampleLink={`${window.location.protocol}//${window.location.host}/${tenant}/reports/eos${sampleLink}`}
      />
    );
  }

  const reportHeaderTitle = customTitlePrefix
    ? `${customTitlePrefix} - ${processGroupName}`
    : (customTitle ?? tenant.toUpperCase());

  return (
    <ReportStyles>
      <ErrorBoundary>
        {readyForShifts ? <ReportsShiftsGetter {...{ machemetryIds }} /> : null}
        <PrintableReportGlobalStyles />
        <PrinterOnlyRavenLogo />
        <LastNWeekGetter
          numWeeks={numOfWeeks}
          machemetryIds={machemetryIds}
          ge={ge}
          lt={lt}
          shouldSkipQuery={!shouldFetchLastNWeeksData}
        />
        {/* try to fetch from askr on some pages, then fallback to midgard if askr times out */}
        {!haveShifts ? null : shouldFetchFromAskr && !askrTimedOut ? (
          <>
            <AskrPnlBreakdownGetter
              range={{ ge, lt }}
              {...{ machemetryIds, setPnlBreakdownData }}
            />
            <AskrPnlBreakdownGetter
              range={{ ge, lt }}
              machemetryIds={machemetryIds}
              setPnlBreakdownData={setPnlBreakdownsByProcess}
              shouldFilterByMachemetryId={true}
            />
            <AskrPnlBreakdownGetter
              range={{ ge, lt }}
              machemetryIds={machemetryIds}
              setPnlBreakdownData={setPnlBreakdownsBySKU}
              shouldSkipQuery={shouldSkipSkuData}
              filter={PnlBreakdownFiltersEnum.sku}
              shouldFilterByMachemetryId={true}
            />
            <AskrPnlBreakdownGetter
              range={{ ge, lt }}
              machemetryIds={machemetryIds}
              shouldSkipQuery={!shouldFetchDataByShift}
              setPnlBreakdownData={setPnlBreakdownsByShift}
              filter={PnlBreakdownFiltersEnum.shiftName}
            />
            <AskrPnlBreakdownGetter
              range={{ ge, lt }}
              machemetryIds={machemetryIds}
              shouldSkipQuery={!shouldFetchDataByShiftAndProcess}
              setPnlBreakdownData={setPnlBreakdownByShiftAndProcess}
              filter={PnlBreakdownFiltersEnum.shiftName}
              shouldFilterByMachemetryId={true}
            />
          </>
        ) : (
          <>
            <MidgardPnlBreakdownGetter
              range={{ ge, lt }}
              {...{ machemetryIds, setPnlBreakdownData }}
              shiftsToExclude={
                shiftsToExclude.length > 0 ? shiftsToExclude : undefined
              }
            />
            <MidgardPnlBreakdownGetter
              range={{ ge, lt }}
              machemetryIds={machemetryIds}
              setPnlBreakdownData={setPnlBreakdownsByProcess}
              shouldFilterByMachemetryId={true}
              shiftsToExclude={
                shiftsToExclude.length > 0 ? shiftsToExclude : undefined
              }
            />
            <MidgardPnlBreakdownGetter
              range={{ ge, lt }}
              machemetryIds={machemetryIds}
              setPnlBreakdownData={setPnlBreakdownsBySKU}
              shouldSkipQuery={shouldSkipSkuData}
              filter={PnlBreakdownFiltersEnum.sku}
              shouldFilterByMachemetryId={true}
              shiftsToExclude={
                shiftsToExclude.length > 0 ? shiftsToExclude : undefined
              }
            />
            <MidgardPnlBreakdownGetter
              range={{ ge, lt }}
              machemetryIds={machemetryIds}
              shouldSkipQuery={!shouldFetchDataByShift}
              setPnlBreakdownData={setPnlBreakdownsByShift}
              filter={PnlBreakdownFiltersEnum.shiftName}
              shiftsToExclude={
                shiftsToExclude.length > 0 ? shiftsToExclude : undefined
              }
            />
            <MidgardPnlBreakdownGetter
              range={{ ge, lt }}
              machemetryIds={machemetryIds}
              shouldSkipQuery={!shouldFetchDataByShiftAndProcess}
              setPnlBreakdownData={setPnlBreakdownByShiftAndProcess}
              filter={PnlBreakdownFiltersEnum.shiftName}
              shouldFilterByMachemetryId={true}
              shiftsToExclude={
                shiftsToExclude.length > 0 ? shiftsToExclude : undefined
              }
            />
          </>
        )}
      </ErrorBoundary>
      {isDoneLoading ? (
        <>
          <ReportHeader
            title={reportHeaderTitle}
            subheader={customSubTitle ?? t("reportTitle")}
            rightSideComponent={
              <div className="actionButtons">
                {shouldShowPrint && <PrintButton />}
                {shouldShowDownload && (
                  <CSVDownloadButton
                    fileName={`Reports_${reportHeaderTitle}_${reportHeader}_${format(
                      ge,
                      "yyyy_MM_dd"
                    )}-${format(lt, "yyyy_MM_dd")}`}
                    rows={[
                      // parse all this data into csv-friendly format:
                      // {
                      //   pnlBreakdownData,
                      //   pnlBreakdownsByProcess,
                      //   pnlBreakdownsBySKU,
                      //   pnlBreakdownsByShift,
                      //   pnlBreakdownByShiftAndProcess,
                      //   lastNWeekData,
                      // },
                      // total breakdown
                      parseBreakdownToCSVRow(pnlBreakdownData, "OVERALL"),
                      //  parse each process breakdown
                      ...(() => {
                        const rows: CSVRow[] = [];
                        for (const processBreakdown of pnlBreakdownsByProcess) {
                          const nextRow = parseBreakdownToCSVRow(
                            processBreakdown,
                            `PROCESS_${processBreakdown.processNames.join(",")}`
                          );
                          rows.push(nextRow);
                        }
                        return rows;
                      })(),
                      // parse each sku breakdown
                      ...(() => {
                        if (shouldSkipSkuData) {
                          return [];
                        }
                        const rows: CSVRow[] = [];
                        for (const skuBreakdown of pnlBreakdownsBySKU) {
                          rows.push(
                            parseBreakdownToCSVRow(
                              skuBreakdown,
                              `PROCESS_${skuBreakdown.processNames.join(
                                ","
                              )}__SKU_${skuBreakdown.sku}`
                            )
                          );
                        }
                        return rows;
                      })(),
                      // parse each shift breakdown
                      ...(() => {
                        if (!shouldFetchDataByShift) {
                          return [];
                        }
                        const rows: CSVRow[] = [];
                        for (const shiftBreakdown of pnlBreakdownsByShift) {
                          rows.push(
                            parseBreakdownToCSVRow(
                              shiftBreakdown,
                              `SHIFT_${shiftBreakdown.shiftName}`
                            )
                          );
                        }
                        return rows;
                      })(),
                      // parse each shift+process breakdown
                      ...(() => {
                        if (!shouldFetchDataByShiftAndProcess) {
                          return [];
                        }
                        const rows: CSVRow[] = [];
                        for (const shiftProcessBreakdown of pnlBreakdownByShiftAndProcess) {
                          rows.push(
                            parseBreakdownToCSVRow(
                              shiftProcessBreakdown,
                              `SHIFT_PROCESS_${
                                shiftProcessBreakdown.shiftName
                              }_${shiftProcessBreakdown.machemetryIds.join(
                                ","
                              )}`
                            )
                          );
                        }
                        return rows;
                      })(),
                    ]}
                  />
                )}
                {shouldShowTranslate && <LanguageSelector />}
                {/* Make empty div height of what PrintIcon would be to keep the header a consistent size */}
                {!shouldShowPrint && !shouldShowTranslate && (
                  <div style={{ height: "48px" }} />
                )}
              </div>
            }
          />
          <PrintableReportStyles $isWindows={isWindows}>
            {children}
            {/* Link to Trends Page */}
            {showTrendsForMultiDay && isMultiday ? (
              <div className="timelineButton">
                <FlightTrendsButton />
              </div>
            ) : flightTimelineLinkGroup ? (
              // Link to timline page
              <div className="timelineButton">
                <FlightTimelineButton {...{ flightTimelineLinkGroup }} />
              </div>
            ) : (
              // No link
              <div style={{ paddingBottom: "2em" }} />
            )}
          </PrintableReportStyles>
        </>
      ) : failedToLoad ? (
        <ReportFailedToLoadMessage />
      ) : (
        <>
          <LinearProgress value={percentLoaded} />
          <LoadingCircle
            logAfterTimeout={{
              logData: {
                pnlBreakdownData,
                shiftsData,
                shiftsDataWithinRange,
                pnlBreakdownByShiftAndProcess,
                areAllPnlBreakdownByShiftAndProcessFetched,
                ...(!areAllPnlBreakdownByShiftAndProcessFetched && {
                  "❗ stillMissingPnlBreakdownByShiftAndProcess":
                    shiftNamesWithinRange.filter(
                      name =>
                        !pnlBreakdownByShiftAndProcess.find(
                          p => p?.shiftName && p.shiftName === name
                        )
                    ),
                }),
                areAllPnlBreakdownsByProcessFetched,
                pnlBreakdownsByProcess,
                ...(!areAllPnlBreakdownsByProcessFetched && {
                  "❗ stillMissingPnlBreakdownsByProcess": machemetryIds.filter(
                    id =>
                      !pnlBreakdownsByProcess.find(p =>
                        p?.machemetryIds.includes(id)
                      )
                  ),
                }),
                areAllPnlBreakdownsBySkuFetched,
                pnlBreakdownsBySKU,
                ...(!areAllPnlBreakdownsBySkuFetched && {
                  "❗ stillMissingPnlBreakdownsBySku": machemetryIds.filter(
                    id =>
                      !pnlBreakdownsBySKU.find(p =>
                        p?.machemetryIds.includes(id)
                      )
                  ),
                }),
                areAllPnlBreakdownsByShiftFetched,
                pnlBreakdownsByShift,
                ...(!areAllPnlBreakdownsByShiftFetched && {
                  "❗ stillMissingPnlBreakdownsByShift": machemetryIds.filter(
                    id =>
                      pnlBreakdownsByShift.find(p =>
                        p?.machemetryIds.includes(id)
                      )
                  ),
                }),
                areAllLastNWeeksPnlBreakdownsFetched,
                lastNWeekData,
                ...(!areAllLastNWeeksPnlBreakdownsFetched && {
                  "❗ stillMissingLastNWeeksPnlBreakdowns":
                    machemetryIds.filter(
                      id =>
                        !lastNWeekData.find(p => p?.machemetryIds.includes(id))
                    ),
                }),
                areFlightLinksDoneLoading,
                flightDashboardLinkGroup,
                flightDashboardLinks,
                shouldWaitForFlightLinks,
              },
              delay: 10 * 1000,
            }}
          />
        </>
      )}
    </ReportStyles>
  );
}

function ReportsShiftsGetter({ machemetryIds }: { machemetryIds: string[] }) {
  const { ge, lt } = useGeAndLtFromUrl();
  const [, setShiftsData] = useAtom(shiftsEosReportAtom);
  useGetShifts({ ge: `${ge}`, lt: `${lt}` }, machemetryIds[0], {
    bufferQueryRange: true,
    shouldNotUseCache: true,
    onCompleted: data => {
      setShiftsData(data);
    },
  });
  return null;
}

function getPercentLoaded({
  numPnlBreakdownsByProcessLoaded,
  numPnlBreakdownsByProcessRequired,
  numPnlBreakdownsBySkuLoaded,
  numPnlBreakdownsBySkuRequired,
  numPnlBreakdownsByShiftLoaded,
  numPnlBreakdownsByShiftRequired,
  numLastNWeeksPnlBreakdownsLoaded,
  numLastNWeeksPnlBreakdownsRequired,
  numPnlBreakdownByShiftAndProcessLoaded,
  numPnlBreakdownByShiftAndProcessRequired,
  numFlightLinksLoaded,
  numFlightLinksRequired,
}: {
  numPnlBreakdownsByProcessLoaded: number;
  numPnlBreakdownsByProcessRequired: number;
  numPnlBreakdownsBySkuLoaded: number;
  numPnlBreakdownsBySkuRequired: number;
  numPnlBreakdownsByShiftLoaded: number;
  numPnlBreakdownsByShiftRequired: number;
  numLastNWeeksPnlBreakdownsLoaded: number;
  numLastNWeeksPnlBreakdownsRequired: number;
  numPnlBreakdownByShiftAndProcessLoaded: number;
  numPnlBreakdownByShiftAndProcessRequired: number;
  numFlightLinksLoaded: number;
  numFlightLinksRequired: number;
}) {
  const percentLoadedNum =
    Math.min(
      numPnlBreakdownsByProcessLoaded,
      numPnlBreakdownsByProcessRequired
    ) +
    Math.min(numPnlBreakdownsBySkuLoaded, numPnlBreakdownsBySkuRequired) +
    Math.min(numPnlBreakdownsByShiftLoaded, numPnlBreakdownsByShiftRequired) +
    Math.min(
      numLastNWeeksPnlBreakdownsLoaded,
      numLastNWeeksPnlBreakdownsRequired
    ) +
    Math.min(
      numPnlBreakdownByShiftAndProcessLoaded,
      numPnlBreakdownByShiftAndProcessRequired
    ) +
    Math.min(numFlightLinksLoaded, numFlightLinksRequired);

  const percentLoadedDenom =
    numPnlBreakdownsByProcessRequired +
    numPnlBreakdownsBySkuRequired +
    numPnlBreakdownsByShiftRequired +
    numLastNWeeksPnlBreakdownsRequired +
    numPnlBreakdownByShiftAndProcessRequired +
    numFlightLinksRequired;

  const percentLoaded = (100 * percentLoadedNum) / percentLoadedDenom;
  return percentLoaded;
}

const MAX_REPORT_LOAD_TIME = 60 * 1000; // ? 10 seconds?

/** after a timeout, if it hasn't finished loading yet, it failed to load */
function useFailedToLoad(isDoneLoading: boolean) {
  const [failedToLoad, setFailedToLoad] = useState(false);
  const [errorOverlayState, setErrorOverlayState] = useAtom(
    errorOverlayStateAtom
  );
  const [servicesStatus] = useServiceStatus();
  useEffect(() => {
    if (errorOverlayState.error) {
      // if already showing an overlay, don't wipe it out with a new message
      return;
    }
    const timeout = setTimeout(() => {
      setFailedToLoad(true);
      setErrorOverlayState(prev => ({
        ...prev,
        error: true,
        message: (
          <>
            {`Report failed to load within ${MAX_REPORT_LOAD_TIME / 1000} seconds.`}
          </>
        ),
      }));
    }, MAX_REPORT_LOAD_TIME);
    if (isDoneLoading || servicesStatus.midgard.breakerOpen) {
      clearTimeout(timeout);
    }
    return () => clearTimeout(timeout);
  }, [
    isDoneLoading,
    servicesStatus.midgard.breakerOpen,
    setErrorOverlayState,
    errorOverlayState.error,
  ]);
  return failedToLoad;
}

function ReportFailedToLoadMessage() {
  return (
    <div
      style={{
        margin: "auto",
        textAlign: "center",
        width: "fit-content",
        padding: "4em",
        lineHeight: "1.5em",
      }}
    >
      <p>
        Report failed to load — please{" "}
        <a
          href="#"
          style={{ cursor: "pointer" }}
          onClick={() => window.location.reload()}
        >
          try again.
        </a>{" "}
      </p>
      <p>
        If the issue persists,{" "}
        <a href="https://raven.ai/contact-us/" target="_blank" rel="noreferrer">
          contact support
        </a>
        .
      </p>
    </div>
  );
}
